
"use client";

import { useState, useMemo, useRef, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
// import "swiper/css";
// import "swiper/css/navigation";
import ProgramCard from "./Programcard";
import { ChevronLeft, ChevronRight } from "lucide-react";

const extractText = (richText) => {
  if (!richText) return "";
  if (typeof richText === "string") return richText.trim();
  if (Array.isArray(richText)) {
    return richText
      .map((block) => extractText(block))
      .join(" ")
      .trim();
  }
  if (richText.children && Array.isArray(richText.children)) {
    return richText.children
      .map((child) => {
        if (child.type === "text" && child.text) return child.text;
        if (child.type === "link" && child.children?.[0]?.text) {
          return child.children[0].text;
        }
        return extractText(child);
      })
      .join("")
      .trim();
  }
  return "";
};

const ProgramsLevelSection = ({ programlevel }) => {
  const { levels, programsByLevel } = useMemo(() => {
    if (!Array.isArray(programlevel)) {
      return { levels: [], programsByLevel: {} };
    }

    const map = {};
    programlevel.forEach((group) => {
      const programs = group.programename?.data || group.programename || [];
      programs.forEach((progData) => {
        const prog = progData.attributes || progData;
        const levelKey = extractText(prog?.Programename) || "Level";
        if (!map[levelKey]) map[levelKey] = [];
        map[levelKey].push({
          id: progData.id || prog.id,
          coursename: prog.coursename,
          coursedetails: prog.coursedetails,
          otherdetails: prog.otherdetails,
          courseimage: prog.courseimage,
          courseUrl: prog.courseUrl,
        });
      });
    });

    const levelList = Object.keys(map).map((key, index) => ({
      id: key,
      label: key,
      programs: map[key],
      order: index,
    }));
    return { levels: levelList, programsByLevel: map };
  }, [programlevel]);

  if (!levels?.length) return null;

  const [activeLevelId, setActiveLevelId] = useState(levels[0].id);
  const activeLevel =
    levels.find((lvl) => lvl.id === activeLevelId) || levels[0];
  const activePrograms = programsByLevel[activeLevel.id] || [];

  const prevRef = useRef(null);
  const nextRef = useRef(null);

  return (
    <section className="w-full page-container md:4 lg:mt-14">
      <div className="relative">
        <div className="mt-14 mb-2">
          <h2 className="text-2xl font-saansBold md:text-3xl text-black mb-8">
            Discover Courses Across All Levels
          </h2>
        </div>
        <div className="flex items-center justify-between mb-8">
          <div className="inline-flex gap-2 p-1">
            {levels.map((level) => (
              <button
                key={level.id}
                onClick={() => setActiveLevelId(level.id)}
                className={`px-4 md:px-6 py-2 text-xs md:text-sm font-medium bg-[#FFA201] text-black cursor-pointer ${
                  activeLevelId === level.id ? "" : "opacity-70"
                }`}
              >
                {level.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <button
              ref={prevRef}
              className="cursor-pointer inline-flex h-10 w-10 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-700 shadow-sm hover:bg-gray-50 hover:border-gray-400 transition-all duration-300"
              aria-label="Previous"
            >
              <ChevronLeft className="h-5 w-5" strokeWidth={2.5} />
            </button>
            <button
              ref={nextRef}
              className="cursor-pointer inline-flex h-10 w-10 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-700 shadow-sm hover:bg-gray-50 hover:border-gray-400 transition-all duration-300"
              aria-label="Next"
            >
              <ChevronRight className="h-5 w-5" strokeWidth={2.5} />
            </button>
          </div>
        </div>
        {activePrograms.length ? (
          <Swiper
            modules={[Navigation]}
            spaceBetween={24}
            slidesPerView={1}
            breakpoints={{
              768: { slidesPerView: 3 },
            }}
            navigation={{
              prevEl: prevRef.current,
              nextEl: nextRef.current,
            }}
            onInit={(swiper) => {
              if (prevRef.current)
                swiper.params.navigation.prevEl = prevRef.current;
              if (nextRef.current)
                swiper.params.navigation.nextEl = nextRef.current;
              swiper.navigation.init();
              swiper.navigation.update();
            }}
          >
            {activePrograms.map((program) => (
              <SwiperSlide key={program.id} className="h-full flex">
                <ProgramCard program={program} />
              </SwiperSlide>
            ))}
          </Swiper>
        ) : (
          <p className="text-sm text-gray-600">
            Programs will be updated soon for this level.
          </p>
        )}
      </div>
    </section>
  );
};

export default ProgramsLevelSection;
